import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const getItemLabel = (item) =>
  String(
    item?.name ??
      item?.itemDetails ??
      item?.itemName ??
      item?.label ??
      item?.title ??
      ""
  );

const getItemId = (item) => item?.id ?? item?._id ?? item?.value ?? getItemLabel(item);

const ItemSuggestionsPortal = ({ open, results = [], rowIndex, onSelect }) => {
  const markerRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);
  const [visible, setVisible] = useState(false);

  const getAnchorInput = useCallback(() => {
    const activeElement =
      typeof document !== "undefined" ? document.activeElement : null;

    if (
      activeElement?.tagName === "INPUT" &&
      markerRef.current?.closest("td")?.contains(activeElement)
    ) {
      return activeElement;
    }

    return markerRef.current?.closest("td")?.querySelector("input") || null;
  }, []);

  const updatePosition = useCallback(() => {
    const input = inputRef.current || getAnchorInput();
    if (!input || typeof window === "undefined") return;

    inputRef.current = input;
    const rect = input.getBoundingClientRect();
    const viewportPadding = 12;
    const gap = 4;
    const availableBelow = window.innerHeight - rect.bottom - viewportPadding;
    const availableAbove = rect.top - viewportPadding;
    const openAbove = availableBelow < 140 && availableAbove > availableBelow;
    const availableSpace = openAbove ? availableAbove : availableBelow;
    const maxHeight = Math.max(120, Math.min(260, availableSpace - gap));

    setMenuStyle({
      left: rect.left,
      top: openAbove
        ? Math.max(viewportPadding, rect.top - maxHeight - gap)
        : Math.min(rect.bottom + gap, window.innerHeight - viewportPadding),
      width: rect.width,
      maxHeight,
    });
  }, [getAnchorInput]);

  useEffect(() => {
    if (!open || results.length === 0) {
      setVisible(false);
      return undefined;
    }

    inputRef.current = getAnchorInput();
    setVisible(Boolean(inputRef.current));
    updatePosition();

    const schedulePositionUpdate = () => {
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(updatePosition);
      } else {
        updatePosition();
      }
    };

    window.addEventListener("resize", schedulePositionUpdate);
    window.addEventListener("scroll", schedulePositionUpdate, true);

    return () => {
      window.removeEventListener("resize", schedulePositionUpdate);
      window.removeEventListener("scroll", schedulePositionUpdate, true);
    };
  }, [getAnchorInput, open, results.length, updatePosition]);

  useEffect(() => {
    if (!visible) return undefined;

    const handleOutsideInteraction = (event) => {
      if (event.key === "Escape") {
        setVisible(false);
        return;
      }

      if (event.type !== "mousedown") return;

      const clickedMenu = menuRef.current?.contains(event.target);
      const clickedInput = inputRef.current?.contains(event.target);

      if (!clickedMenu && !clickedInput) {
        setVisible(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideInteraction);
    document.addEventListener("keydown", handleOutsideInteraction);

    return () => {
      document.removeEventListener("mousedown", handleOutsideInteraction);
      document.removeEventListener("keydown", handleOutsideInteraction);
    };
  }, [visible]);

  const menu =
    visible && menuStyle ? (
      <ul
        ref={menuRef}
        className="item-suggestions-portal"
        style={{
          position: "fixed",
          top: menuStyle.top,
          left: menuStyle.left,
          width: menuStyle.width,
          maxHeight: menuStyle.maxHeight,
          zIndex: 99999,
          listStyleType: "none",
          padding: 0,
          margin: 0,
          overflowY: "auto",
          backgroundColor: "#fff",
          border: "1px solid #dfe6ee",
          borderRadius: "8px",
          boxShadow: "0 16px 36px rgba(15, 23, 42, 0.18)",
        }}
        onMouseDown={(event) => event.preventDefault()}
      >
        {results.map((result, index) => {
          const label = getItemLabel(result);

          return (
            <li
              key={`${getItemId(result)}-${index}`}
              onMouseDown={() => {
                if (onSelect) {
                  onSelect(label, getItemId(result), rowIndex, result);
                }
                setVisible(false);
              }}
              style={{
                padding: "10px 12px",
                cursor: "pointer",
                color: "#111827",
                fontSize: "14px",
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.backgroundColor = "#408dfb";
                event.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.backgroundColor = "";
                event.currentTarget.style.color = "#111827";
              }}
            >
              {label}
            </li>
          );
        })}
      </ul>
    ) : null;

  return (
    <>
      <span ref={markerRef} style={{ display: "none" }} />
      {menu && typeof document !== "undefined"
        ? createPortal(menu, document.body)
        : menu}
    </>
  );
};

export default ItemSuggestionsPortal;
